import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalSection,
    StandardModalSectionTitle,
    StandardModalState,
    Toast,
    Modal,
} from "src/components";
import MultipleInvitesForm from "./MultipleInvitesForm";
import SingleInviteForm from "./SingleInviteForm";
import AppInviteMethod from "src/components/AppInviteMethod";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import InviteSuccessModal from "src/components/AppInvite/InviteSuccessModal";
import RequestDocumentsSection from "src/scenes/EmployeeList/components/RequestDocumentsSection";

const InviteFooterNote = styled(StandardModalDescription)`
    margin: 0;
`;

const SendInviteFormModal = (props) => {
    const { showDialog, handleClose, users, token, documentTypes = [], onDocumentsSaved } = props;
    const [selectedMethod, setSelectedMethod] = useState("email");
    const [selectedDocumentUuids, setSelectedDocumentUuids] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailInviteInProgress, setisEmailInviteInProgress] = useState(false);
    const [isSmsInviteInProgress, setIsSmsInviteInProgress] = useState(false);
    const [isEmailInvite200response, setIsEmailInvite200response] = useState(false);
    const [isSmsInvite200response, setIsSmsInvite200response] = useState(false);
    const [showInviteSuccessModal, setShowInviteSuccessModal] = useState(false);
    const [inviteSuccessType, setInviteSuccessType] = useState("email");

    useEffect(() => {
        if (!showDialog) return;

        const existingDocumentUuids = [
            ...new Set(
                (users || []).flatMap(user =>
                    (user.requiredDocuments || []).map(document => document.uuid)
                )
            )
        ];
        setSelectedDocumentUuids(existingDocumentUuids);
    }, [showDialog, users]);

    const handleSwitchMethod = (value) => {
        setSelectedMethod(value);
    };

    const handleFormSubmit = async () => {
        setIsLoading(true);
        const data = {
            method: selectedMethod,
            users: users
           
        };
        await handleSendInvitation(data);
    };
    
    const requestPayload = (users) => ({
        users,
        documentUuids: selectedDocumentUuids
    });

    const sendInviteByEmail = async (users) => {
        const url = `${API_URL}/company/sendInvites/email`;
        setisEmailInviteInProgress(true);
        setIsEmailInvite200response(false);
        try {
            const response = await axios.post(
                url,
                requestPayload(users),
                { headers: { Authorization: "Bearer " + token } }
            );
            if (response.data.subject !== null && response.data.subject === "rate-limit") {
                Modal.open("Email Rate-Limited", response.data.info);
                return false;
            }

            setIsEmailInvite200response(true);
            return true;
        } finally {
            setisEmailInviteInProgress(false);
        }
    };

    const sendInviteBySms = async (users) => {
        const url = `${API_URL}/company/sendInvites/sms`;
        setIsSmsInviteInProgress(true);
        setIsSmsInvite200response(false);
        try {
            const response = await axios.post(
                url,
                requestPayload(users),
                { headers: { Authorization: "Bearer " + token } }
            );
            if (response.data.subject !== null && response.data.subject === "rate-limit") {
                Modal.open("Sms Rate-Limited", response.data.info);
                return false;
            }

            setIsSmsInvite200response(true);
            return true;
        } finally {
            setIsSmsInviteInProgress(false);
        }
    };
    
    const handleSendInviteByEmail = async (stringifiedUsersArray) => {
        return await sendInviteByEmail(stringifiedUsersArray);
    };

    const handleSendInviteBySms = async (stringifiedUsersArray) => {
        return await sendInviteBySms(stringifiedUsersArray);
    };

    const openInviteSuccessModal = (type) => {
        setShowInviteSuccessModal(true);
        setInviteSuccessType(type);
    };

    const handleSendInvitation = async ({ method, users }) => {
        if (isEmailInviteInProgress || isSmsInviteInProgress) {
            return;
        }

        if (!users.length) {
            /** TODO: change `user` to be employee, client or license client */
            return Toast.error("No user found. Create at least one before sending email app invites.");
        }

        const stringifiedUsersArray = JSON.stringify(users);
        try {
            let inviteWasSent = false;
            if (method === "email") {
                inviteWasSent = await handleSendInviteByEmail(stringifiedUsersArray);
            } else if (method === "sms") {
                inviteWasSent = await handleSendInviteBySms(stringifiedUsersArray);
            } else if (method === "both") {
                const emailWasSent = await trySendEmailAndFailSilently(stringifiedUsersArray);
                const smsWasSent = await trySendSmsAndFailSilently(stringifiedUsersArray);
                inviteWasSent = emailWasSent || smsWasSent;
            }

            if (!inviteWasSent) {
                setIsLoading(false);
                return;
            }

            openInviteSuccessModal(method);
            onDocumentsSaved?.();
            

        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
            handleClose(false);
        }
    };

    const trySendEmailAndFailSilently = async (stringifiedUsersArray) => {
        try {
            return await handleSendInviteByEmail(stringifiedUsersArray);
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message); //TODO: new mockup

            //scenario to when we needed to throw errors with Toast
            if (error.getStatus() !== 403) { //When error is not "You can send one email invite every 60 minutes"
                throw error; //re-throw
            }
            return false;
        }
    };

    const trySendSmsAndFailSilently = async (stringifiedUsersArray) => {
        try {
            return await handleSendInviteBySms(stringifiedUsersArray);
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message); //TODO: new mockup

            //scenario to when we needed to throw errors with Toast
            // throw err;
            return false;
        }
    };

    return (
        <>
            { showInviteSuccessModal ?
                <InviteSuccessModal
                    showDialog={showInviteSuccessModal}
                    handleClose={handleClose}
                    type={inviteSuccessType}
                    isEitherInviteSuccessful={isEmailInvite200response || isSmsInvite200response}
                />
                :
                <InviteFormModal
                    showDialog={showDialog}
                    handleClose={handleClose}
                    isLoading={isLoading}
                    users={users}
                    handleSwitchMethod={handleSwitchMethod}
                    handleFormSubmit={handleFormSubmit}
                    documentTypes={documentTypes}
                    selectedDocumentUuids={selectedDocumentUuids}
                    setSelectedDocumentUuids={setSelectedDocumentUuids}
                />
            }

        </>
    );
};

const InviteFormModal = ({
    showDialog,
    handleClose,
    isLoading,
    users,
    handleSwitchMethod,
    handleFormSubmit,
    documentTypes = [],
    selectedDocumentUuids = [],
    setSelectedDocumentUuids = () => {},
}) => {
    const closeModal = () => handleClose(false);

    return (
        <StandardModal
            open={showDialog}
            onClose={closeModal}
            title="Send App Invite"
            description="Please choose your preferred method of sending the app invitation to the designated user(s)."
            widthMode="compactWide"
            paperStyle={{
                minHeight: isLoading ? "min(37.5rem, calc(100vh - 2rem))" : undefined
            }}
            footer={!isLoading && (
                <StandardModalActionGroup justifyContent="space-between" style={{ width: "100%" }}>
                    <InviteFooterNote>
                        Kindly be advised that email invitations may be directed to the spam folder. It is recommended to notify users if they do not receive the invitation.
                    </InviteFooterNote>
                    <StandardModalActionGroup>
                        <StandardModalActionButton
                            width="8.75rem"
                            onClick={closeModal}
                        >
                            Cancel
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            $variant="primary"
                            width="8.75rem"
                            type="submit"
                            onClick={() => handleFormSubmit()}
                        >
                            Send Invite
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </StandardModalActionGroup>
            )}
        >
            {isLoading ? (
                <StandardModalState minHeight="28rem">
                    <LottieLoadingIndicator />
                </StandardModalState>
            ) : (
                <>
                    <StandardModalSection>
                        <StandardModalSectionTitle>
                            {users.length > 1 ? "Selected Users" : "Selected User"}
                        </StandardModalSectionTitle>
                        {users.length > 1 ? (
                            <MultipleInvitesForm users={users} />
                        ) : (
                            <SingleInviteForm user={users[0]} />
                        )}
                    </StandardModalSection>

                    {documentTypes.length > 0 && (
                        <StandardModalSection>
                            <RequestDocumentsSection
                                documentTypes={documentTypes}
                                selectedDocuments={selectedDocumentUuids}
                                setSelectedDocuments={setSelectedDocumentUuids}
                            />
                        </StandardModalSection>
                    )}

                    <StandardModalSection>
                        <AppInviteMethod setAppInviteMethod={handleSwitchMethod} />
                    </StandardModalSection>
                </>
            )}
        </StandardModal>
    );
};

export default SendInviteFormModal;
